import { Trophy } from "lucide-react";
import type { ExamSuccessStoryView } from "@/services/marketplaceService";

interface ExamSuccessStoriesProps {
  examSuccessStories: ExamSuccessStoryView[];
}

const achievementLabels: Record<string, string> = {
  SCORE_ABOVE:       "score above",
  SCORE_IMPROVEMENT: "improve scores by",
  EXAM_QUALIFIED:    "qualify for",
  TOP_RANK:          "achieve top rank in",
  PASS_RATE:         "pass",
};

const timeframeLabels: Record<string, string> = {
  LAST_6_MONTHS:  "in the last 6 months",
  LAST_1_YEAR:    "in the last year",
  LAST_2_YEARS:   "in the last 2 years",
  LAST_5_YEARS:   "in the last 5 years",
  CAREER_TO_DATE: "over their career",
};

function buildStoryText(story: ExamSuccessStoryView): string {
  const achievementLabel = achievementLabels[story.achievement] ?? story.achievement;
  const timeframeLabel = timeframeLabels[story.timeframe] ?? story.timeframe;
  const examPart = story.examName ? ` in ${story.examName}` : "";
  const thresholdPart = story.threshold ? ` ${story.threshold}` : "";
  return `helped ${achievementLabel}${thresholdPart}${examPart} ${timeframeLabel}`;
}

export function ExamSuccessStories({ examSuccessStories }: ExamSuccessStoriesProps) {
  if (!examSuccessStories || examSuccessStories.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-5 w-1 rounded-full bg-gradient-to-b from-amber-400 to-yellow-500" />
        <h2 className="text-xl font-bold text-foreground">Exam Success</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {examSuccessStories.map((story, i) => (
          <div
            key={i}
            className="rounded-xl bg-slate-100 dark:bg-slate-800/50 p-4 flex gap-4 items-start shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-3xl font-extrabold text-foreground leading-none">
                {story.studentCount}
              </p>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mt-0.5 mb-1.5">
                {story.studentCount === 1 ? "student" : "students"}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {buildStoryText(story)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
